import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { AuditEntry } from "../lib/types.js";

/** Turn a snake_case action ("licence_renewed", "billing_invoice.paid") into a
 * readable label, and colour the destructive ones. */
function actionLabel(action: string): string {
  return action.replace(/_/g, " ").replace(/\./g, " · ");
}
function isDestructive(action: string): boolean {
  return /revoke|delete|block|terminate|suspend|refuse|seat_limit/.test(action);
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

export function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  async function load(q = "", before: string | null = null, append = false) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (before) params.set("before", before);
      const res = await api.get<{ entries: AuditEntry[]; nextBefore: string | null }>(
        `/admin/audit-log${params.toString() ? `?${params}` : ""}`
      );
      setEntries((prev) => (append ? [...prev, ...res.entries] : res.entries));
      setNextBefore(res.nextBefore);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load the audit log");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => load(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div>
      <h1>Audit log</h1>
      <p style={{ color: "var(--muted)", fontSize: 13, margin: "0 0 10px" }}>
        Every admin and automated action — issue, renew, revoke, block, billing — newest first.
      </p>
      <input
        type="search"
        placeholder="Search by who, what, or target…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ width: "100%", maxWidth: 480, margin: "4px 0 8px" }}
        aria-label="Search audit log"
      />
      {error && <p className="error-text">{error}</p>}
      <table className="data-table">
        <thead>
          <tr>
            <th>When</th>
            <th>Who</th>
            <th>Action</th>
            <th>Target</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id}>
              <td style={{ whiteSpace: "nowrap" }}>{fmt(e.created_at)}</td>
              <td>{e.actor}</td>
              <td>
                <span className={`status-badge ${isDestructive(e.action) ? "status-revoked" : ""}`}>
                  {actionLabel(e.action)}
                </span>
              </td>
              <td>
                {e.target_type ? (
                  <>
                    {e.target_type} <code>{e.target_id ?? "—"}</code>
                  </>
                ) : (
                  "—"
                )}
              </td>
              <td className="message-cell" title={e.metadata ? JSON.stringify(e.metadata) : ""}>
                {e.metadata ? JSON.stringify(e.metadata) : "—"}
              </td>
            </tr>
          ))}
          {entries.length === 0 && !loading && (
            <tr>
              <td colSpan={5}>{search.trim() ? "No entries match your search." : "No activity logged yet."}</td>
            </tr>
          )}
        </tbody>
      </table>
      {nextBefore && (
        <button className="secondary" disabled={loading} onClick={() => load(search, nextBefore, true)} style={{ marginTop: 12 }}>
          {loading ? "Loading…" : "Load older"}
        </button>
      )}
    </div>
  );
}
